import Bluebird from "bluebird";
import I18next from "i18next";
import FSBackend from "i18next-fs-backend";
import * as path from "path";
import { initReactI18next } from "react-i18next";
import * as fs from "./fs";
import getVortexPath from "./getVortexPath";
let debugging = false;
let currentLanguage = "en";
const fallbackTFunc = (str) => Array.isArray(str) ? str[0].toString() : str.toString();
let actualT = fallbackTFunc;
export { fallbackTFunc };
let missingKeys = { common: {} };
class MultiBackend {
    static type = "backend";
    mOptions;
    mServices;
    mCurrentBackend;
    mLastReadLanguage;
    mBackendType;
    constructor(services, options) {
        this.init(services, options);
    }
    init(services, options) {
        this.mOptions = options;
        this.mServices = services;
    }
    read(language, namespace, callback) {
        const { backendType, extPath } = this.backendType(language);
        if (backendType !== this.mBackendType ||
            (backendType === "extension" && language !== this.mLastReadLanguage)) {
            this.mCurrentBackend = this.initBackend(backendType, extPath);
        }
        this.mLastReadLanguage = language;
        this.mCurrentBackend.read(language, namespace, callback);
    }
    initBackend(type, extPath) {
        const res = new FSBackend();
        let basePath;
        if (type === "bundled") {
            basePath = this.mOptions.bundled;
        }
        else if (type === "custom") {
            basePath = this.mOptions.user;
        }
        else {
            basePath = extPath;
        }
        res.init(this.mServices, {
            loadPath: path.join(basePath, "{{lng}}", "{{ns}}.json"),
            ident: 2,
        });
        this.mBackendType = type;
        return res;
    }
    backendType(language) {
        try {
            // translations from the user directory (custom installs or in-development)
            fs.statSync(path.join(this.mOptions.user, language));
            return { backendType: "custom" };
        }
        catch (err) {
            // extension-provided
            const ext = this.mOptions.translationExts().find((iter) => {
                try {
                    fs.statSync(path.join(iter.path, language));
                    return true;
                }
                catch (err) {
                    return false;
                }
            });
            if (ext !== undefined) {
                return { backendType: "extension", extPath: ext.path };
            }
            try {
                // finally, see if we have the language bundled
                fs.statSync(path.join(this.mOptions.bundled, language));
                return { backendType: "bundled" };
            }
            catch (err) {
                return { backendType: "custom" };
            }
        }
    }
}
class HighlightPP {
    name;
    type;
    constructor() {
        this.type = "postProcessor";
        this.name = "HighlightPP";
    }
    process(value, key, options, translator) {
        if (value.startsWith("TT:")) {
            console.trace("duplicate translation", key, value);
        }
        return "TT:" + value.toUpperCase();
    }
}
/**
 * initialize the internationalization library
 *
 * @export
 * @param {string} language
 * @returns {I18next.I18n}
 */
function init(language, translationExts) {
    // reset to english if the language isn't valid
    try {
        new Date().toLocaleString(language);
    }
    catch (err) {
        language = "en";
    }
    currentLanguage = language;
    const i18nObj = I18next;
    if (process.env.HIGHLIGHT_I18N === "true") {
        i18nObj.use(new HighlightPP());
    }
    i18nObj.use(MultiBackend).use(initReactI18next);
    return Bluebird.resolve(i18nObj.init({
        lng: language,
        fallbackLng: "en",
        fallbackNS: "common",
        ns: [
            "common",
            "collection",
            "mod_management",
            "download_management",
            "profile_management",
            "nexus_integration",
            "gamemode_management",
            "extension_manager",
            "health_check",
        ],
        defaultNS: "common",
        nsSeparator: ":::",
        keySeparator: "::",
        debug: false,
        postProcess: process.env.HIGHLIGHT_I18N === "true" ? "HighlightPP" : false,
        react: {
            // afaict this is simply broken at this time. With this enabled the React.Suspense will
            // render the fallback on certain operations after the UI has been started,
            // why I don't know, and that unmounts all components in the dom but it doesn't seem to
            // fire the componentDidUnmount lifecycle functions meaning we can't stop delayed
            // operations that will then break since the component is unmounted
            useSuspense: false,
        },
        saveMissing: debugging,
        saveMissingTo: "current",
        missingKeyHandler: (lng, ns, key, fallbackValue) => {
            if (missingKeys[ns] === undefined) {
                missingKeys[ns] = {};
            }
            missingKeys[ns][key] = key;
        },
        interpolation: {
            escapeValue: false,
        },
        backend: {
            bundled: getVortexPath("locales"),
            user: path.normalize(path.join(getVortexPath("userData"), "locales")),
            translationExts,
        },
    }))
        .tap((tFunc) => {
        actualT = tFunc;
    })
        .then((tFunc) => Bluebird.resolve({
        i18n: i18nObj,
        tFunc,
    }))
        .catch((error) => ({
        i18n: i18nObj,
        tFunc: fallbackTFunc,
        error,
    }));
}
export function getCurrentLanguage() {
    return currentLanguage;
}
export function changeLanguage(lng, cb) {
    currentLanguage = lng;
    return I18next.changeLanguage(lng, cb);
}
export function globalT(key, options) {
    return actualT(key, options);
}
export function debugTranslations(enable) {
    debugging = enable !== undefined ? enable : !debugging;
    missingKeys = { common: {} };
    init(I18next.language, () => []);
}
export function getMissingTranslations() {
    return missingKeys;
}
export class TString {
    mKey;
    mOptions;
    constructor(key, options, namespace) {
        this.mKey = key;
        this.mOptions = options ?? {};
        if (this.mOptions.ns === undefined) {
            this.mOptions.ns = namespace;
        }
    }
    get key() {
        return this.mKey;
    }
    get options() {
        return this.mOptions;
    }
    toString() {
        return this.mKey;
    }
}
export const laterT = (key, optionsOrDefault, options) => {
    if (typeof optionsOrDefault === "string") {
        return new TString(key, options, "common");
    }
    else {
        return new TString(key, optionsOrDefault, "common");
    }
};
/**
 * translate an input string. If key is a string or string array, this just
 * forwards the parameters to the t function.
 * If it is an ITString object, will translate using with the parameters stored
 * within
 * @param t the actual translation function to invok
 * @param key translation key, keys or ITString object
 * @param options translations options. this will take precedence over those specified at
 *                the time the ITString was created
 * @param onlyTString if set to true and the key is a string, assume it's already the translated
 *                    string and don't translate again. This is mostly for backwards compatibility
 */
export function preT(t, key, options, onlyTString) {
    if ([undefined, null].includes(key)) {
        return "";
    }
    if (typeof key === "string") {
        if (onlyTString === true) {
            return key;
        }
        else {
            return t(key, options);
        }
    }
    else if (Array.isArray(key)) {
        return t(key, options);
    }
    else {
        return t(key.key, { ...key.options, ...(options ?? {}) });
    }
}
export default init;
